import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    Button,
    TextField,
    IconButton,
    InputAdornment,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ClearIcon from '@mui/icons-material/Clear';
import { makeStyles } from '@mui/styles';
import { ArrowForwardIosSharp } from '@mui/icons-material';
import { SimplePromptsIcon } from '../assets/SvgIcons';
import { updatePrompt } from '../services/apiService';
import { showToast } from '../reducers/uiReducer';
import { setSelectedTileId, setIsActiveChat } from '../reducers/uiReducer';
import { addMessage, selectChatContext } from '../reducers/messagesReducer';

import { sendMessage } from '../services/apiService';
import { setLoading } from '../reducers/loadingReducer';
import PromptPopover from './PromptPopover';

const useStyles = makeStyles(() => ({
    container: {
        height: 0,
        overflow: 'hidden',
        transition: 'height 0.3s ease',
    },
    containerExpanded: {
        height: 60,
    },
    icon: {
        height: 56,
        width: 56,
        marginRight: 8,
        padding: 8,
        transition:
            'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease',
    },
    iconHovered: {
        transform: 'scale(1.1)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        backgroundColor: 'rgb(231, 247, 255)',
    },
    iconPressed: {
        boxShadow: 'none',
        backgroundColor: 'rgb(186, 218, 235)',
    },
    shiftLeft: {
        transform: 'translateX(-8px)',
    },
    shiftRight: {
        transform: 'translateX(8px)',
    },
}));

const PromptBarRibbon = ({ config, setPromptCallBack, handleOpen }) => {
    const classes = useStyles();
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [pressedIndex, setPressedIndex] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [hoveredListItem, setHoveredListItem] = useState(null); // State to track hovered list item
    const [searchTerm, setSearchTerm] = useState(''); // State to track search input
    const [expanded, setExpanded] = useState(false); // State to track container expansion

    const savedPrompts = useSelector(
        (state) => state.auth.user.preferences.prompts || [],
    );
    const dispatch = useDispatch();
    const chatContext = useSelector(selectChatContext);

    React.useEffect(() => {
        setExpanded(true);
    }, []);

    const handleRunPrompt = async (prompt) => {
        setAnchorEl(null); // Close the flyout menu
        dispatch(setSelectedTileId(null));
        dispatch(setIsActiveChat(true));
        dispatch(setLoading(true));

        const newUserMessage = {
            role: 'user',
            componentType: 'text',
            content: [{ type: 'text', text: prompt.promptText }],
        };

        const updatedChatContext = [...chatContext, newUserMessage];

        dispatch(addMessage(newUserMessage));

        try {
            const response = await sendMessage(updatedChatContext);

            if (response && response.choices?.[0]?.message) {
                const assistantMessage = {
                    role: 'assistant',
                    content: [
                        { type: 'text', text: response.choices[0].message.content },
                    ],
                };

                dispatch(addMessage(assistantMessage));
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            const errorMessage = {
                role: 'error',
                content: [{ type: 'text', text: 'Service Unavailable.' }],
            };
            dispatch(addMessage(errorMessage));
            dispatch(
                showToast({ message: 'Failed to send message', severity: 'error' }),
            );
        } finally {
            dispatch(setLoading(false));
        }
    };

    if (!config || !config.tiles) {
        console.error('Invalid config passed to PromptBarRibbon:', config);
        return null;
    }

    const handleFlyoutOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFlyoutClose = () => {
        setAnchorEl(null);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const open = Boolean(anchorEl);
    const id = open ? 'flyout-popover' : undefined;

    const filteredPrompts = savedPrompts.filter((prompt) =>
        prompt.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box display="flex" className={`${classes.container} ${expanded ? classes.containerExpanded : ''}`}>
            {config.tiles.map((tile, index) => {
                let shiftClass = '';
                if (hoveredIndex !== null) {
                    if (index < hoveredIndex) {
                        shiftClass = classes.shiftLeft;
                    } else if (index > hoveredIndex) {
                        shiftClass = classes.shiftRight;
                    }
                }
                return (
                    <Box
                        key={index}
                        className={`${classes.icon} ${hoveredIndex === index ? classes.iconHovered : ''} ${pressedIndex === index ? classes.iconPressed : ''} ${shiftClass}`}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onMouseDown={() => setPressedIndex(index)}
                        onMouseUp={() => setPressedIndex(null)}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpen(tile);
                        }}
                    >
                        {tile.smallIcon || <InfoIcon />}
                    </Box>
                );
            })}
            <PromptPopover
                id={id}
                open={open}
                anchorEl={anchorEl}
                handleClose={handleFlyoutClose}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                handleClearSearch={handleClearSearch}
                filteredPrompts={filteredPrompts}
                handleRunPrompt={handleRunPrompt}
            />
        </Box>
    );
};

export default PromptBarRibbon;